import partners, { getPartnerUrlHost, isAgreementUrlBranded } from '../../../partners';
import { isAggregatorConsumer, isHosted } from '../../../utilities/utils';
import config from '@cof/ukweb-config/lib/clientConfig';
import { pCRValue } from '../../../utilities/constants';

const addUrlParamConditionally = (paramName: any, paramValue: any, url: any) => {
    if (paramValue) url.searchParams.set(paramName, paramValue);
};

const setProductCode = (ccsCodeQuotation: any, brand: any, aggregatorConsumerName: any, urlWithParams: any) => {
    if (ccsCodeQuotation && (brand !== partners.DEFAULT || isAggregatorConsumer(aggregatorConsumerName))) {
        urlWithParams.searchParams.set('p', ccsCodeQuotation);
    }
};

const isLocalhostHost = (host: any) => host === 'localhost' || `${host}`.startsWith('localhost:');

const assertAllowedScheme = (url: any) => {
    // Only https is allowed. http is permitted solely for local dev (http://localhost:3001).
    if (url.protocol === 'https:') return;
    if (url.protocol === 'http:' && isLocalhostHost(url.host)) return;
    throw new Error('agreementUrl rejected: disallowed scheme');
};

const validateAndUpdateAgreementUrl = (brand: any, agreementUrl: any) => {
    // Scheme allowlist runs unconditionally (fail closed) to block javascript:/data: XSS payloads.
    assertAllowedScheme(agreementUrl);

    const agreementUrlHost = config.get('agreementUrlHost');
    if (!agreementUrlHost) {
        return agreementUrl;
    }

    if (brand !== partners.DEFAULT && !isAgreementUrlBranded(brand, agreementUrl, agreementUrlHost)) {
        agreementUrl.host = getPartnerUrlHost(brand, agreementUrlHost);
    }

    // Host anchoring: the FINAL host (after any brand rewrite) must be a trusted host.
    // localhost (any port) is always allowed — redirecting to localhost is not an attack and
    // prod hosts are never localhost (consistent with the scheme check above).
    const allowedHosts = new Set([agreementUrlHost, getPartnerUrlHost(brand, agreementUrlHost)]);
    if (!isLocalhostHost(agreementUrl.host) && !allowedHosts.has(agreementUrl.host)) {
        throw new Error('agreementUrl rejected: disallowed host');
    }

    return agreementUrl;
};

export const agreementUrlBuilder = ({
    agreementUrl,
    sParameterQuotation,
    ccsCodeQuotation,
    brand,
    aggregatorConsumerName,
    pcr,
    channel,
    campaign,
    vendorCode
}: any) => {
    const urlWithParams = validateAndUpdateAgreementUrl(brand, new URL(agreementUrl));

    setProductCode(ccsCodeQuotation, brand, aggregatorConsumerName, urlWithParams);
    if (!isHosted(brand) || isAggregatorConsumer(aggregatorConsumerName)) {
        addUrlParamConditionally('pcr', pcr, urlWithParams);
    } else if (brand !== partners.DEFAULT) {
        urlWithParams.searchParams.set('pcr', pCRValue);
    }
    addUrlParamConditionally('s', sParameterQuotation, urlWithParams);
    addUrlParamConditionally('channel', channel, urlWithParams);
    addUrlParamConditionally('campaign', campaign, urlWithParams);
    addUrlParamConditionally('vendorCode', vendorCode, urlWithParams);
    return urlWithParams;
};
